import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { DatosReciboPDF, LineaRecibo } from '@/lib/pdfReciboPago'
import { aplicacionesService } from '@/services/aplicacionesService'

/** El estudio lo aporta el hook desde `configuracion_estudio`, igual que en el estado de cuenta. */
export type DatosReciboSinEstudio = Omit<DatosReciboPDF, 'estudio'>

export interface ReciboCompleto {
  datos: DatosReciboSinEstudio
  /**
   * Adónde mandarlo. Va afuera de `datos` a propósito: el mail del cliente no se imprime en el
   * recibo, y meterlo ahí lo pondría en un documento que el cliente reenvía.
   */
  emailCliente: string | null
  clienteId: string
}

/**
 * Arma los datos del recibo de pago (ORR-336) leyendo SIEMPRE de la base.
 *
 * El recibo se emite en el momento del cobro, pero podría reenviarse o regenerarse después. Tomar
 * los datos del formulario en el alta y de la base en el reenvío daría dos caminos que pueden
 * divergir, y el recibo reenviado dejaría de ser idéntico al original. La adjudicación queda
 * persistida en `movimientos_aplicaciones` (RPC de ORR-260), así que con el id del pago alcanza.
 */
export const recibosService = {
  async obtenerDatosRecibo(
    client: SupabaseClient<Database>,
    pagoId: string,
    organizacionId: string,
  ): Promise<ReciboCompleto> {
    const { data: pago, error: errorPago } = await client
      .from('movimientos')
      .select('id, fecha, concepto, monto, haber, cliente_id, numero_recibo, tipo')
      .eq('id', pagoId)
      .eq('organizacion_id', organizacionId)
      .single()

    if (errorPago) throw errorPago
    if (!pago) throw new Error('No se encontró el pago')
    if (pago.tipo !== 'Pago') {
      throw new Error('El movimiento no es un pago: no se le puede emitir recibo')
    }
    if (!pago.cliente_id) {
      throw new Error('El pago no tiene cliente asociado')
    }

    const { data: cliente, error: errorCliente } = await client
      .from('vista_clientes_completos')
      .select('nombre, nombre_completo, cuit, condicion_iva, email')
      .eq('id', pago.cliente_id)
      .single()

    if (errorCliente) throw errorCliente

    const aplicaciones = await aplicacionesService.obtenerAplicacionesPago(client, pagoId)

    const lineas: LineaRecibo[] = (aplicaciones ?? [])
      .map((aplicacion) => {
        // El embed llega como objeto o como array de uno según cómo PostgREST resuelva la relación.
        const deuda = Array.isArray(aplicacion.movimientos)
          ? aplicacion.movimientos[0]
          : aplicacion.movimientos
        return {
          periodo: deuda?.mes_anio ?? null,
          concepto: deuda?.concepto ?? 'Obligación sin concepto',
          importe: Number(aplicacion.monto_aplicado),
          fechaDeuda: deuda?.fecha ?? '',
        }
      })
      // Cronológico por la obligación que se cancela. Sin esto salen en orden de carga, y un pago
      // que cubre tres meses se lee "Julio, Junio, Agosto", que parece un error del recibo. Se
      // ordena por la fecha de la deuda y no por `mes_anio`, que es texto en español y no ordena.
      .sort((a, b) => a.fechaDeuda.localeCompare(b.fechaDeuda))
      .map(({ fechaDeuda: _fechaDeuda, ...linea }) => linea)

    const emailCliente = cliente?.email?.trim() || null

    return {
      datos: {
        cliente: {
          nombre: cliente?.nombre_completo || cliente?.nombre || 'Cliente',
          cuit: cliente?.cuit ?? undefined,
          condicionIva: cliente?.condicion_iva ?? undefined,
        },
        pago: {
          fecha: pago.fecha,
          // `haber` es el lado contable del pago; `monto` lo replica. Se prefiere `haber` y se cae
          // a `monto` para las filas viejas que lo tengan en cero.
          monto: Number(pago.haber) || Number(pago.monto) || 0,
          concepto: pago.concepto,
          numero: pago.numero_recibo,
        },
        lineas,
      },
      emailCliente,
      clienteId: pago.cliente_id,
    }
  },
}
